import { Link, router } from '@inertiajs/react'
import { Fragment, useMemo } from 'react'
import AppLayout from '../../components/AppLayout'
import BreakdownScoreBar from '../../components/BreakdownScoreBar'
import { useTheme } from '../../contexts/ThemeContext'

const GRADE_COLORS = { '優': '#5b6ba0', '良': '#4a7c59', '可': '#c9963f', '要注意': '#c0463a' }
const CAT_COLORS = {
  '旬×推し':      '#4a7c59',
  '人気':          '#c9963f',
  '珍しさ指標':   '#c0463a',
  '重複状況':      '#5b6ba0',
  'セットバランス': '#7058a3',
}

function pct(v) { return v == null ? '—' : `${(v * 100).toFixed(1)}%` }
function ptDiff(curr, prev) {
  if (curr == null || prev == null) return null
  return (curr - prev) * 100
}
function gradeOf(v) {
  const p = (v || 0) * 100
  return p >= 80 ? '優' : p >= 70 ? '良' : p >= 60 ? '可' : '要注意'
}

function KpiCard({ label, value, prev, sub, C }) {
  const diff = ptDiff(value, prev)
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px', minWidth: '160px' }}>
      <div style={{ fontSize: '10px', color: C.inkMuted, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: C.ink, fontFamily: "'JetBrains Mono', monospace", marginTop: '4px' }}>
        {pct(value)}
      </div>
      {diff != null && (
        <div style={{ fontSize: '11px', color: diff >= 0 ? '#4a7c59' : '#c0463a', fontWeight: 600, marginTop: '2px' }}>
          前年比 {diff >= 0 ? '+' : ''}{diff.toFixed(1)}pt
        </div>
      )}
      {sub && <div style={{ fontSize: '10px', color: C.inkMuted, marginTop: '4px' }}>{sub}</div>}
    </div>
  )
}

function GradeDistribution({ grades, total, C }) {
  if (!total) return null
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px', minWidth: '240px', flex: 1 }}>
      <div style={{ fontSize: '10px', color: C.inkMuted, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
        グレード分布（お届け）
      </div>
      <div style={{ display: 'flex', height: '20px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${C.line}` }}>
        {['優', '良', '可', '要注意'].map(g => {
          const n = grades[g] || 0
          const w = (n / total) * 100
          if (w === 0) return null
          return (
            <div key={g} title={`${g}: ${n}件 (${w.toFixed(1)}%)`}
              style={{ width: `${w}%`, background: GRADE_COLORS[g], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff', fontWeight: 700 }}>
              {w > 8 ? `${g} ${n}` : ''}
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '11px', color: C.inkMuted, flexWrap: 'wrap' }}>
        {['優', '良', '可', '要注意'].map(g => (
          <span key={g} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '8px', height: '8px', background: GRADE_COLORS[g], borderRadius: '2px' }} />
            {g} {grades[g] || 0}
          </span>
        ))}
      </div>
    </div>
  )
}

function WeeklyTrend({ rows, C }) {
  if (!rows.length) return <div style={{ padding: '20px', color: C.inkMuted, fontSize: '12px' }}>データなし</div>

  const W = 1000, H = 220
  const pL = 40, pR = 20, pT = 16, pB = 40
  const cW = W - pL - pR
  const cH = H - pT - pB
  const xStep = cW / Math.max(rows.length - 1, 1)
  const toY = v => pT + cH - v * cH

  const pathFor = (key, color) => {
    const d = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${pL + i * xStep},${toY(r[key])}`).join(' ')
    return <path d={d} fill="none" stroke={color} strokeWidth={2} />
  }

  const monthMarkers = []
  let lastMonth = null
  rows.forEach((r, i) => {
    const m = new Date(r.week).getMonth() + 1
    if (m !== lastMonth) {
      monthMarkers.push({ i, m })
      lastMonth = m
    }
  })

  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.ink }}>週次スコアトレンド</span>
        <span style={{ fontSize: '11px', color: C.inkMuted, display: 'flex', gap: '12px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><svg width={20} height={4}><line x1={0} y1={2} x2={20} y2={2} stroke="#4a7c59" strokeWidth={2} /></svg>お届け</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><svg width={20} height={4}><line x1={0} y1={2} x2={20} y2={2} stroke="#c9963f" strokeWidth={2} /></svg>魅力</span>
        </span>
      </div>
      <svg width={W} height={H} style={{ display: 'block', maxWidth: '100%' }}>
        {[0, 0.25, 0.5, 0.75, 1.0].map(g => (
          <g key={g}>
            <line x1={pL} y1={toY(g)} x2={pL + cW} y2={toY(g)} stroke={C.lineSoft} strokeWidth={1} strokeDasharray="2,3" />
            <text x={pL - 4} y={toY(g) + 3} fontSize={9} fill="#999" textAnchor="end">{(g * 100).toFixed(0)}</text>
          </g>
        ))}
        {monthMarkers.map(({ i, m }) => (
          <g key={i}>
            <line x1={pL + i * xStep} y1={pT} x2={pL + i * xStep} y2={pT + cH} stroke={C.lineSoft} strokeWidth={1} />
            <text x={pL + i * xStep} y={H - 18} fontSize={9} fill="#888" textAnchor="middle">{m}月</text>
          </g>
        ))}

        {pathFor('avg_delivery', '#4a7c59')}
        {pathFor('avg_appeal',   '#c9963f')}

        {rows.map((r, i) => (
          <g key={r.week}>
            <Link href={`/weekly_evaluations/${r.week}`}>
              <circle cx={pL + i * xStep} cy={toY(r.avg_delivery)} r={3} fill="#4a7c59" style={{ cursor: 'pointer' }} />
            </Link>
            <Link href={`/weekly_evaluations/${r.week}`}>
              <circle cx={pL + i * xStep} cy={toY(r.avg_appeal)} r={3} fill="#c9963f" style={{ cursor: 'pointer' }} />
            </Link>
          </g>
        ))}
      </svg>
      <div style={{ fontSize: '10px', color: C.inkMuted, marginTop: '4px' }}>
        点をクリックでその週の評価一覧へ
      </div>
    </div>
  )
}

function CriterionHeatmap({ criteria, months, data, C }) {
  const cellW = 56, cellH = 28
  const labelW = 180

  const colorFor = v => {
    if (v == null) return '#eee'
    const t = Math.max(0, Math.min(1, v))
    const r = Math.round(192 + (74 - 192) * t)
    const g = Math.round(70 + (124 - 70) * t)
    const b = Math.round(58 + (89 - 58) * t)
    return `rgb(${r},${g},${b})`
  }

  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px', overflowX: 'auto' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: C.ink, marginBottom: '8px' }}>指標 × 月 ヒートマップ（調整後スコア平均）</div>
      <div style={{ display: 'grid', gridTemplateColumns: `${labelW}px repeat(${months.length}, ${cellW}px)`, gap: '2px' }}>
        <div />
        {months.map(m => (
          <div key={m} style={{ fontSize: '10px', color: C.inkMuted, textAlign: 'center', fontWeight: 600 }}>{m}月</div>
        ))}
        {criteria.map(c => (
          <Fragment key={c.key}>
            <div style={{ fontSize: '11px', color: C.ink, display: 'flex', alignItems: 'center', gap: '6px', paddingRight: '6px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: CAT_COLORS[c.category] || C.accent }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
            </div>
            {months.map(m => {
              const v = data[c.key]?.[m]
              return (
                <div key={m}
                  title={`${c.name} / ${m}月: ${pct(v)}`}
                  style={{
                    background: colorFor(v),
                    height: `${cellH}px`,
                    borderRadius: '3px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', color: v == null ? '#aaa' : '#fff', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                  }}>
                  {v == null ? '—' : (v * 100).toFixed(0)}
                </div>
              )
            })}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

function PlanRankingTable({ title, rows, accentColor, C }) {
  if (!rows || !rows.length) return null
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px', flex: 1, minWidth: '360px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: accentColor, marginBottom: '8px', letterSpacing: '0.05em' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {rows.map((r, i) => (
          <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '20px minmax(120px, 1fr) 1.4fr 56px', gap: '8px', alignItems: 'center' }}>
            <span style={{ color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{i + 1}</span>
            <Link href={`/weekly_evaluations/${r.week}/set_plans/${r.id}`}
              style={{ color: C.accent, textDecoration: 'none', fontSize: '11px', overflow: 'hidden' }}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.set_type_name || r.plan_code}</div>
              <div style={{ color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: '9px' }}>{r.week}</div>
            </Link>
            <BreakdownScoreBar value={r.score} breakdown={r.breakdown} C={C} height={20} showTotal={false} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: accentColor, fontWeight: 700, textAlign: 'right', fontSize: '12px' }}>{pct(r.score)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function SetTypeRanking({ rows, C }) {
  if (!rows.length) return null
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: C.ink, marginBottom: '8px' }}>セット種別ランキング</div>
      <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 90px 90px 70px', gap: '4px 12px', alignItems: 'center', fontSize: '11px' }}>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>#</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>セット種別</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>お届け平均</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>魅力平均</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>件数</div>
        {rows.map((r, i) => (
          <Fragment key={r.code || r.name}>
            <div style={{ color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace" }}>{i + 1}</div>
            <div style={{ color: C.ink }}>
              {r.name}
              <span style={{ marginLeft: '6px', color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: '10px' }}>{r.code}</span>
            </div>
            <div style={{ color: gradeOf(r.avg_delivery) === '要注意' ? '#c0463a' : C.ink, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{pct(r.avg_delivery)}</div>
            <div style={{ color: C.ink, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>{pct(r.avg_appeal)}</div>
            <div style={{ color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>{r.count}</div>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

function WeeklyTable({ rows, C }) {
  if (!rows.length) return null
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', padding: '14px 16px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: C.ink, marginBottom: '8px' }}>週次一覧（詳細リンク）</div>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 110px 100px 100px 70px', gap: '4px 12px', alignItems: 'center', fontSize: '11px' }}>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>週</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>月</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>お届け平均</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>魅力平均</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>件数</div>
        <div style={{ color: C.inkMuted, fontWeight: 700, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }} />
        {rows.map(r => {
          const d = new Date(r.week)
          return (
            <Fragment key={r.week}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", color: C.ink }}>{r.week}</div>
              <div style={{ color: C.inkMuted }}>{d.getMonth() + 1}月</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", color: C.ink, textAlign: 'right', fontWeight: 600 }}>{pct(r.avg_delivery)}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", color: C.ink, textAlign: 'right' }}>{pct(r.avg_appeal)}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", color: C.inkMuted, textAlign: 'right' }}>{r.count}</div>
              <Link href={`/weekly_evaluations/${r.week}`} style={{ color: C.accent, textDecoration: 'none', fontSize: '11px', fontWeight: 600 }}>
                詳細 →
              </Link>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default function AnnualRetrospectiveShow({
  current_year, available_years, set_type_id, set_types,
  kpi, weekly_trend, criterion_monthly, set_type_ranking,
}) {
  const { theme: C } = useTheme()

  const reload = useMemo(() => (year, stid) => {
    const params = { year }
    if (stid) params.set_type_id = stid
    router.get('/annual_retrospective', params, { preserveScroll: false })
  }, [])

  return (
    <AppLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <div>
          <div style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '22px', fontWeight: 500, color: C.ink, marginBottom: '4px' }}>
            年間振り返り — {current_year}年
          </div>
          <p style={{ color: C.inkMuted, fontSize: '12px', margin: 0 }}>
            年度内のセットプラン全件を集計。スコアトレンド・指標別ヒートマップから週次詳細にドリルダウンできます。
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select value={current_year} onChange={e => reload(parseInt(e.target.value), set_type_id)}
            style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '4px', border: `1px solid ${C.line}`, background: C.paper, color: C.ink }}>
            {available_years.map(y => <option key={y} value={y}>{y}年</option>)}
          </select>
          <select value={set_type_id || ''} onChange={e => reload(current_year, e.target.value || null)}
            style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '4px', border: `1px solid ${C.line}`, background: C.paper, color: C.ink, maxWidth: '200px' }}>
            <option value="">全セット種別</option>
            {set_types.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <KpiCard label="お届けスコア平均" value={kpi.avg_delivery} prev={kpi.avg_delivery_prev} C={C} />
        <KpiCard label="魅力スコア平均"   value={kpi.avg_appeal}   prev={kpi.avg_appeal_prev}   C={C} />
        <KpiCard label="評価件数"          value={null} sub={`${kpi.total} 件`} C={C} />
        <GradeDistribution grades={kpi.grades} total={kpi.total} C={C} />
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <PlanRankingTable title="お届けスコア — ベスト TOP 5"   rows={kpi.delivery_top}    accentColor="#4a7c59" C={C} />
        <PlanRankingTable title="お届けスコア — ワースト TOP 5" rows={kpi.delivery_bottom} accentColor="#c0463a" C={C} />
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <PlanRankingTable title="魅力スコア — ベスト TOP 5"   rows={kpi.appeal_top}    accentColor="#4a7c59" C={C} />
        <PlanRankingTable title="魅力スコア — ワースト TOP 5" rows={kpi.appeal_bottom} accentColor="#c0463a" C={C} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <WeeklyTrend rows={weekly_trend} C={C} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <CriterionHeatmap {...criterion_monthly} C={C} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <WeeklyTable rows={weekly_trend} C={C} />
        <SetTypeRanking rows={set_type_ranking} C={C} />
      </div>
    </AppLayout>
  )
}
